/**
 * Session storage for the dashboard token.
 *
 * Form Lock's trick is worth keeping: read the JWT's own `exp` and treat an expired token as
 * absent. Without it the UI keeps showing a signed-in shell and offering actions the server will
 * reject, and the user experiences a working app that has silently stopped working. This is not
 * signature validation — the server does that — it is the UI declining to assert something that
 * is already false.
 */

import type { AuthResult, Role } from './types'

const STORAGE_KEY = 'devpulse:session'
const CHANGE_EVENT = 'devpulse:session-changed'

export interface StoredSession {
  token: string
  userId: string
  organizationId: string
  organizationName: string
  email: string
  displayName: string
  role: Role
}

/** The instant this token expires, according to the token. Null when it cannot be read. */
function expiryOf(token: string): number | null {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null

    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: number }).exp

    return typeof exp === 'number' ? exp * 1000 : null
  } catch {
    // An unreadable token is treated as valid and left to the server to reject. Signing someone
    // out over a parsing detail would be deciding against them for no good reason.
    return null
  }
}

export function getSession(): StoredSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null

    const session = JSON.parse(raw) as StoredSession
    const expiry = session.token ? expiryOf(session.token) : null

    if (expiry !== null && expiry <= Date.now()) {
      // Expired: remove it as well as ignoring it, so the next screen does not repeat the mistake.
      window.localStorage.removeItem(STORAGE_KEY)
      return null
    }

    return session
  } catch {
    return null
  }
}

export function getToken(): string | null {
  return getSession()?.token ?? null
}

export function saveSession(result: AuthResult): void {
  const session: StoredSession = {
    token: result.token,
    userId: result.userId,
    organizationId: result.organizationId,
    organizationName: result.organizationName,
    email: result.email,
    displayName: result.displayName,
    role: result.role,
  }

  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session))
  } catch {
    // Storage unavailable: the session lives for this tab only, which still works.
  }

  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

export function clearSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Nothing to do.
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT))
}

export function onSessionChange(handler: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, handler)
  // Signing out in one tab should sign out the others.
  window.addEventListener('storage', handler)
  return () => {
    window.removeEventListener(CHANGE_EVENT, handler)
    window.removeEventListener('storage', handler)
  }
}

/** Roles are ordered by privilege, so every check is "at least". */
const ORDER: Record<Role, number> = {
  Viewer: 0,
  Developer: 1,
  EngineeringManager: 2,
  Admin: 3,
  Owner: 4,
}

export function atLeast(role: Role | undefined, minimum: Role): boolean {
  if (!role) return false
  return ORDER[role] >= ORDER[minimum]
}
